import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { MatchPhone } from "refsix-js-models";

import { RootState } from "../../../redux/store";
import {
    BoardMetricRankingPublic,
    BoardRecordPublic,
} from "../../../services/venueService";
import {
    formatMetric,
    formatMetricValue,
    initialsOf,
    isMe,
    matchIdForMetricValue,
    METRIC_ICONS,
    metricLabel,
    resolveBoardRows,
    VenueMetric,
} from "../leaderboardModel";
import { BoardGap, BoardRow, BoardRowPlaceholders } from "./BoardRows";

const KEY = "leaderboards.VenuePage.";

export interface RecordHolderCardProps {
    metric: VenueMetric;
    record: BoardRecordPublic | null;
    /** The ranked board for this metric and window. */
    ranking: BoardMetricRankingPublic | null;
    /** The player's own best here, for the comparison row. */
    yourBest: number;
    /**
     * This metric's value on the match the user opened the page from, when there was one. The
     * boards rank personal bests, so the comparison row shows today's result instead — the one
     * number the page cannot otherwise tell them.
     */
    thisMatch?: number | null;
    /** Gold "hall of fame" treatment, used by the all-time window. */
    hallOfFame?: boolean;
    /** Label for the window this record belongs to, shown as the card subtitle. */
    windowLabel: string;
    myDisplayName?: string | null;
    onBoards: boolean;
    /** The player's own matches at this ground, used to resolve a row of theirs to a result. */
    myMatches: MatchPhone[];
    /** Opens a match result. Wired only to rows that belong to the player. */
    onOpenMatch: (matchId: string) => void;
    testId: string;
}

/**
 * A line of the card that becomes a button when it has a match behind it.
 *
 * The record line and the "your best here" line are the two places a number of the player's own
 * appears outside the board rows, and both open the result that set it. They keep their `div`
 * shape when there is nothing to open, so a stranger's record is not a dead button.
 */
const TappableLine: FC<{
    className: string;
    onOpen?: () => void;
    testId?: string;
    children: ReactNode;
}> = ({ className, onOpen, testId, children }) => {
    if (!onOpen) {
        return (
            <div className={className} data-testid={testId}>
                {children}
            </div>
        );
    }
    return (
        <button
            type="button"
            className={`${className} venue-row-tappable`}
            data-testid={testId}
            onClick={onOpen}
        >
            {children}
        </button>
    );
};

const HolderLine: FC<{ onOpen?: () => void; children: ReactNode }> = (
    props
) => <TappableLine className="venue-rcard-holder" {...props} />;

const YourValueRow: FC<{
    onOpen?: () => void;
    testId?: string;
    children: ReactNode;
}> = (props) => <TappableLine className="venue-rcard-you" {...props} />;

/**
 * One venue metric board (SCORZA-739 design handoff, reference/02 `.rcard`).
 *
 * The card header is the record — the number and the device it was set on — and the ranked top
 * five sits underneath it, with the player's own row pinned below a break when they are further
 * down. The holder is row 1 of that board, marked by the gold avatar; a separate holder line
 * above the board only appears when there is no board (a snapshot written before ranked lists
 * existed), where it is the only place the name can go.
 *
 * The source label is not optional decoration. Watch-only GPS and the performance vest have
 * different accuracy profiles, so an unlabelled record is one nobody has a reason to trust.
 */
export const RecordHolderCard: FC<RecordHolderCardProps> = ({
    metric,
    record,
    ranking,
    yourBest,
    thisMatch,
    hallOfFame,
    windowLabel,
    myDisplayName,
    onBoards,
    myMatches,
    onOpenMatch,
    testId,
}) => {
    const { t } = useTranslation();
    const { useImperial } = useSelector(
        (root: RootState) => root.settings.settings
    );

    const mine = isMe(record?.displayName, myDisplayName, onBoards);
    const value = record
        ? formatMetricValue(metric, record.value, useImperial, t)
        : null;
    const board = resolveBoardRows(
        ranking?.rows || [],
        ranking?.rankedCount || 0,
        myDisplayName,
        onBoards
    );
    const yourBestFormatted = yourBest
        ? formatMetricValue(metric, yourBest, useImperial, t)
        : null;

    // Arriving from a match result, the comparison row is that match rather than the best,
    // which the board rows above already rank.
    const fromMatch = typeof thisMatch === "number" && thisMatch > 0;
    const yours = fromMatch ? (thisMatch as number) : yourBest;

    // The number the board already shows for this player: their ranked row — which the server
    // builds as their best submitted value, not their latest — or the row pinned past the
    // stored cut, which carries their local best. Null when the board says nothing about them,
    // and then this row is the only place a number of their own appears.
    const myRow = board.rows.find((row) =>
        isMe(row.displayName, myDisplayName, onBoards)
    );
    const shownBest = myRow
        ? myRow.value
        : board.youState === "outsideCut"
        ? yourBest
        : null;

    // Their best here as far as this page is concerned. Read off the board where it has them,
    // because only submitted matches reach a board: comparing against the local figure instead
    // left this row repeating the ranked row above it.
    const best = shownBest ?? yourBest;

    /**
     * Tapping a number of the player's own opens the match that set it. Nothing is returned for
     * a stranger's row, or for one of their own set on a phone this match never reached.
     */
    const openerFor = (value: number | null | undefined) => {
        const matchId = matchIdForMetricValue(myMatches, metric, value);
        return matchId ? () => onOpenMatch(matchId) : undefined;
    };

    // Whether the match they came from fell short of that best — the one thing the boards above
    // do not already say. Compared at display precision, since the server and the match
    // document need not agree to the metre.
    const sameAsBest =
        formatMetricValue(metric, yours, useImperial, t).value ===
        formatMetricValue(metric, best, useImperial, t).value;
    const belowBest = fromMatch && !sameAsBest && yours < best;

    // Omitted when they hold the record — the row above has their name on it and repeating it
    // reads as a bug — and when there is nothing to add, because this match is their best and
    // the board already shows it.
    const showYours = !mine && (belowBest || shownBest === null);
    const comparison = belowBest ? yours : best;

    // "100+ of 812" when the board is deeper than the server stores, otherwise the plain count.
    // Either way it answers "how big is this board?", which is what makes a rank mean anything.
    const stored = ranking?.rows.length || 0;
    const total = ranking?.rankedCount || 0;
    const rankedCountLabel =
        total <= board.rows.length
            ? null
            : total > stored
            ? t(KEY + "rankedOf", { shown: stored, total })
            : t(KEY + "playersRanked", { count: total });

    return (
        <div
            className={`venue-rcard${hallOfFame ? " venue-rcard-hof" : ""}`}
            data-testid={testId}
        >
            <div className="venue-rcard-top">
                <div className="venue-rcard-label">
                    <span className="venue-rcard-icon" aria-hidden="true">
                        {METRIC_ICONS[metric]}
                    </span>
                    <div>
                        <div className="venue-rcard-title">
                            {metricLabel(metric, t)}
                        </div>
                        <div className="venue-rcard-sub">{windowLabel}</div>
                    </div>
                </div>
                {value && (
                    <div className="venue-rcard-big">
                        <div
                            className="venue-rcard-big-value"
                            data-testid={`${testId}-value`}
                        >
                            {value.value}
                        </div>
                        {value.unit && (
                            <div className="venue-rcard-big-unit">
                                {value.unit}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {!record && (
                <div
                    className="venue-rcard-empty"
                    data-testid={`${testId}-empty`}
                >
                    {t(KEY + "noRecordsYet")}
                </div>
            )}

            {record && (
                <>
                    {board.rows.length === 0 && (
                        <HolderLine
                            onOpen={mine ? openerFor(record.value) : undefined}
                        >
                            <span
                                className="venue-avatar venue-avatar-gold"
                                aria-hidden="true"
                            >
                                {initialsOf(record.displayName)}
                            </span>
                            <span
                                className="venue-rcard-holder-name text-ellipsis"
                                data-testid={`${testId}-holder`}
                            >
                                {record.displayName}
                            </span>
                            <span className="venue-rcard-holder-tag">
                                {t(KEY + "recordHolder")}
                            </span>
                        </HolderLine>
                    )}

                    {!!ranking && (
                        <div
                            className="venue-legend-list"
                            data-testid={`${testId}-rows`}
                        >
                            {board.rows.map((row, index) => {
                                const you = isMe(
                                    row.displayName,
                                    myDisplayName,
                                    onBoards
                                );
                                const rowValue = formatMetricValue(
                                    metric,
                                    row.value,
                                    useImperial,
                                    t
                                );
                                return (
                                    <div key={`${row.rank}-${row.displayName}`}>
                                        {index === board.gapBefore && (
                                            <BoardGap />
                                        )}
                                        <BoardRow
                                            rank={row.rank}
                                            displayName={row.displayName}
                                            value={rowValue.value}
                                            unit={rowValue.unit}
                                            you={you}
                                            gold={row.rank === 1}
                                            onOpen={
                                                you
                                                    ? openerFor(row.value)
                                                    : undefined
                                            }
                                            testId={`${testId}-row`}
                                        />
                                    </div>
                                );
                            })}

                            <BoardRowPlaceholders filled={board.rows.length} />

                            {/* Past the depth the board stores, so there is no rank to show —
                                but the player still gets their number against the board. */}
                            {board.youState === "outsideCut" && (
                                <>
                                    <BoardGap />
                                    <BoardRow
                                        rank={board.outsideCutLabel}
                                        displayName={t(KEY + "you")}
                                        initialsFrom={myDisplayName}
                                        value={
                                            yourBestFormatted
                                                ? yourBestFormatted.value
                                                : t(KEY + "noValueYet")
                                        }
                                        unit={yourBestFormatted?.unit}
                                        you
                                        onOpen={openerFor(yourBest)}
                                        testId={`${testId}-row-you`}
                                    />
                                </>
                            )}
                        </div>
                    )}

                    {rankedCountLabel && (
                        <div
                            className="venue-rcard-gap"
                            data-testid={`${testId}-count`}
                        >
                            {rankedCountLabel}
                        </div>
                    )}

                    {/* Where the player stands. Omitted when they hold the record — the row
                        above already has their name on it and repeating it reads as a bug —
                        and when this match is their best, which the board already shows. */}
                    {showYours && (
                        <YourValueRow
                            onOpen={openerFor(comparison)}
                            testId={`${testId}-you`}
                        >
                            <span className="venue-rcard-you-label">
                                {t(
                                    KEY +
                                        (belowBest
                                            ? "thisMatch"
                                            : "yourBestHere")
                                )}
                            </span>
                            <span className="venue-rcard-you-value">
                                {comparison
                                    ? formatMetric(
                                          metric,
                                          comparison,
                                          useImperial,
                                          t
                                      )
                                    : t(KEY + "noValueYet")}
                            </span>
                        </YourValueRow>
                    )}
                    {!mine && comparison > 0 && record.value > comparison && (
                        <div className="venue-rcard-gap">
                            {t(KEY + "offTheRecord", {
                                gap: formatMetric(
                                    metric,
                                    record.value - comparison,
                                    useImperial,
                                    t
                                ),
                            })}
                        </div>
                    )}
                    {mine && (
                        <div
                            className="venue-rcard-gap"
                            data-testid={`${testId}-yours`}
                        >
                            {t(KEY + "youHoldThisRecord")}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
